/**
 * The offline or "EXE" platform uses [Electron]{@link https://www.electronjs.org/} to build SRT as a standalone application for Windows or Mac. Some notable additions over online functionality include:
 *
 * - Ability to close app, with either a front-end close button, or the following keys:
 *   - `esc` if no password screen enabled
 *   - `shift` + `backspace` to access password screen, then enter password to close app.
 * - Inactivity timeout can be enabled, in which the app will return to the start screen after a certain amount of time with no clicks.
 *
 * Note that offline platform builds will not run in a browser if initialized with the electron import in its entry file.
 *
 * @module core/offline
 */

import * as App from "./app";
import * as View from "./view";
import * as Form from "./form";
import * as Analytics from "./analytics";

/**
 * Default offline configuration settings, which are added to App `config` using [`App.updateConfig`]{@link module:core/app.updateConfig} when this module is loaded.
 *
 * For more information on configuration options, see [`offline` content settings]{@link module:core/config.offline}.
 *
 * @since 3.0.0
 *
 * @type {object}
 *
 * @example
 * {
 *   electron: false,
 *   esc_close: true,
 *   inactivity_timeout: {
 *     enabled: false,
 *     seconds: 30,
 *     exclude: [],
 *   },
 *   password: {
 *     enabled: false,
 *   },
 * }
 */
export const config = {
  electron: typeof electron !== 'undefined' ? electron : false,
  esc_close: true,
  inactivity_timeout: {
    enabled: false,
    seconds: 30,
    exclude: [],
  },
  password: {
    enabled: false,
  },
};

/**
 * Initialize offline functionality.
 *
 * - If provided, makes Electron [`app`]{@link https://www.electronjs.org/docs/api/app} object available in settings as `App.settings.offline.electron`. See offline entry for more information.
 * - [Adds event listeners]{@link module:core/offline~addEventListeners}.
 * - [Adds form actions]{@link module:core/offline~addFormActions}.
 *
 * @method
 *
 * @since 3.0.0
 *
 * @see [`App.updateSettings`]{@link module:core/app.updateSettings}
 * @see [`addEventListeners`]{@link module:core/offline~addEventListeners}
 * @see [`addFormActions`]{@link module:core/offline~addFormActions}
 * @see Used by:
 * @see [**`App.init`**]{@link module:core/app.init} (as platform init)
 */
export const init = () => {
  // add offline event listeners
  addEventListeners();
  // add form actions
  addFormActions();
}

/**
 * Determine if the app is running in the Electron environment. Returns the boolean of the `App.settings.offline.electron` setting, which will be set in an entry, if present.
 *
 * @since 3.0.0
 *
 * @return {boolean}
 *
 * @see Used by:
 * @see [**`closeApp`**]{@link module:core/offline~closeApp}
 */
const isElectron = () => !!App.settings.offline.electron;

/**
 * Functionality to run when app close is triggered.
 *
 * [If running in Electron]{@link module:core/offline~isElectron}, the [`ipcRenderer`]{@link https://www.electronjs.org/docs/api/ipc-renderer} is used to send an `exitApp` channel command to the Electron `main.js` file to quit the app. If not running in Electron (e.g. in staging or preview builds that omit it), an alert is shown instead to notify that the app would have closed after the given action.
 *
 * @since 3.0.0
 *
 * @see [`isElectron`]{@link module:core/offline~isElectron}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/offline~addEventListeners}
 * @see [**`addFormActions`**]{@link module:core/offline~addFormActions}
 */
const closeApp = () => {
  isElectron() ? App.settings.offline.electron.ipcRenderer.send('exitApp') : alert('closing app.');
}

/**
 * Attempt to run a local external app at a provided path, using parameters from the `run_app` [options settings]{@link module:core/config.options} for button field types.
 *
 * [If running in Electron]{@link module:core/offline~isElectron}, the [`ipcRenderer`]{@link https://www.electronjs.org/docs/api/ipc-renderer} is used to send an `openProgram` channel command to the Electron `main.js` file to attempt to start a child process with the application at the provided path. If not running in Electron (e.g. in staging or preview builds that omit it), an alert is shown instead.
 *
 * @since 3.0.2
 * 
 * @param {HTMLElement} element - Selected button element.
 * 
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/offline~addEventListeners}
 */
const runApp = (element) => {
  const option = View.getElementData(element);
  const path = option.run_app.path;
  const parameters = option.run_app.parameters || [];
  isElectron() ? App.settings.offline.electron.ipcRenderer.send('openProgram', path, parameters) : alert('opening requested app.');
};

/**
 * Returns the app the start page after a duration of inactivity (no clicks). Default duration is 30 seconds, but this can be [configured]{@link module:core/config.offline}.
 *
 * If the function is run while on the start page, nothing is done. Otherwise, any previous timeouts are cleared, and a new timeout is set.
 *
 * @since 3.0.0
 *
 * @see [`Analytics.event`]{@link module:core/analytics.event}
 * @see [`View.navigate`]{@link module:core/view.navigate}
 * @see Used by:
 * @see [**`addEventListeners`**]{@link module:core/offline~addEventListeners}
 *
 * @example
 * *click*
 *
 * *wait 30 seconds*
 *
 * --> start page!
 */
const resetInactivityTimer = () => {
  clearTimeout(config.inactivity_timeout.timer);
  // if on an excluded view, no need to do anything else
  let exclude = [App.settings.start, ...App.settings.offline.inactivity_timeout.exclude];
  if (exclude.includes(App.state.current.location)) return;
  // set new timeout
  config.inactivity_timeout.timer = setTimeout(() => {
    Analytics.event('timeout_reset');
    View.navigate('restart');
  }, App.current.settings.offline.inactivity_timeout.seconds * 1000);
}

/**
 * Add [form actions]{@link module:core/form.actions} for offline functionality.
 *
 * - If password screen is enabled, add `passwordClose` form action. This action gets the current value of the password field and compares it to the configured password value. If valid, [app will close]{@link module:core/offline!closeApp}.
 *
 * @since 3.0.0
 *
 * @see [`closeApp`]{@link module:core/offline~closeApp}
 * @see Used by:
 * @see [**`init`**]{@link module:core/offline.init}
 */
const addFormActions = () => {
  // add password form action, if password screen is enabled
  if (App.settings.offline.password.enabled) {
    Form.actions.passwordClose = (form) => {
      const password = form.find('input[type="password"]').val();
      const validate = password === App.settings.offline.password.pass;
      if (validate) closeApp();
      return validate;
    };
  }
};

/**
 * Add offline platform event listeners to app [`container`]{@link module:core/view.container}.
 *
 * - [Run local external application]{@link module:core/offline.runApp} on `[data-run-app]` click.
 * - If password screen is enabled, [navigate forward]{@link module:core/view.navigate} on `shift` + `backspace` keydown.
 * - If password screen is not enabled, [close app]{@link module:core/offline~closeApp} on `.js-closeApp` click and `esc` keydown.
 * - If inactivity timeout is enabled, [reset inactivity timeout]{@link module:core/offline~resetInactivityTimer} on any click.
 *
 * @since 3.0.0
 *
 * @see [`View.navigate`]{@link module:core/view.navigate}
 * @see [`closeApp`]{@link module:core/offline~closeApp}
 * @see [`resetInactivityTimer`]{@link module:core/offline~resetInactivityTimer}
 * @see Used by:
 * @see [**`init`**]{@link module:core/offline.init}
 */
const addEventListeners = () => {
  // open an application on "run app" button click
  View.container.on('click', '[data-run-app]', (event) => {
    runApp(event.currentTarget);
  });

  // if password screen is enabled
  if (App.settings.offline.password.enabled) {
    $(document).on('keydown', (event) => {
      if (event.shiftKey && event.keyCode === View.keycode.backspace) {
        View.navigate('forward', App.settings.offline.password.destination);
      }
    });
  }
  // close app on "close" button press
  else {
    View.container.on('click', '.js-closeApp', () => {
      closeApp();
    });
  }

  // close app on escape key press, if enabled
  if (App.settings.offline.esc_close) {
    $(document).on('keydown', (event) => {
      if (event.key === "Escape" || event.key === "Esc") closeApp();
    });
  }

  // inactivity timeout
  if (App.settings.offline.inactivity_timeout.enabled) {
    View.container.on('click', () => {
      resetInactivityTimer();
    });
  }
};
